import React, { memo, useState } from 'react';
import {
    Box,
    FormControl,
    FormHelperText,
    InputLabel,
    Select as MuiSelect,
    Stack,
    Typography,
} from '@mui/material';
import { NumberField } from '../number-field';
import { useController, useFormContext } from 'react-hook-form';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { SelectProps as MuiSelectProps } from '@mui/material/Select/Select';
import { get } from 'lodash';
import { AgeBands, PaxData } from '@travelity/api';

export type PaxSelectProps = MuiSelectProps & {
    width?: string;
    size?: 'small' | 'medium';
    label: string;
    placeholder?: string;
    required?: boolean;
    onClose?: () => void;
};

export interface PaxPickerProps {
    name: string;
    withDropdown?: boolean;
    selectProps?: PaxSelectProps;
    showErrors?: boolean;
    maxPax?: PaxData;
}

const ageBands = [
    'Adults',
    'Children',
    'Infants',
    'Youth',
    'Seniors',
    'total_in_collectives',
];

const toPlural = (name: string, value: number) => {
    if (name === 'total_in_collectives')
        return value > 1 ? `${value} persons` : '1 person';
    if (value > 1) return `${value} ${name}`;
    if (name === 'children') return `1 child`;
    // TODO remove s
    return `1 ${name.substring(0, name.length - 1)}`;
};

export const formatValue = (value: Record<string, number>) =>
    ageBands
        .map(name => name.toLowerCase())
        .filter(name => value[name])
        .map(name => toPlural(name, value[name]))
        .join(' | ');

export const PaxPicker: React.FC<PaxPickerProps> = memo(props => {
    const {
        name,
        showErrors,
        withDropdown,
        selectProps = {} as PaxSelectProps,
        maxPax,
    } = props;
    const {
        width,
        size,
        required,
        label,
        placeholder,
        disabled,
        multiple,
        onClose,
        ...rest
    } = selectProps;
    const {
        watch,
        control,
        formState: { errors },
    } = useFormContext();

    const availableAgeBands = maxPax
        ? ageBands.filter(ageBand => maxPax[ageBand.toLowerCase() as AgeBands])
        : ageBands;

    const { field } = useController({
        name,
        control,
        defaultValue: {},
    });
    const value = watch(name);
    const [open, setOpen] = useState(false);

    const error =
        showErrors && (get(errors, name)?.message as string | undefined);

    const fields = (
        <Stack gap={1}>
            {availableAgeBands.map(ageBand => (
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={1}
                    key={ageBand}
                >
                    <Typography
                        sx={{ width: 60, color: '#949BAC', fontSize: '12px' }}
                    >
                        {ageBand}
                    </Typography>
                    <NumberField
                        size="small"
                        sx={{ flexGrow: 2 }}
                        name={`${name}.${ageBand.toLowerCase()}`}
                        label=""
                        max={
                            maxPax
                                ? maxPax[ageBand.toLowerCase() as AgeBands]
                                : undefined
                        }
                    />
                </Stack>
            ))}
        </Stack>
    );

    return withDropdown ? (
        <FormControl
            sx={{ width: width || { xs: '100%', sm: '388px' } }}
            error={!!error}
            size={size}
            disabled={disabled}
        >
            <MuiSelect
                open={open}
                onOpen={() => setOpen(true)}
                onClose={() => {
                    field.onChange(value);
                    setOpen(false);
                    onClose?.();
                }}
                required={!!required}
                inputProps={{ required: false }}
                label={label}
                displayEmpty
                IconComponent={KeyboardArrowDownIcon}
                sx={{
                    ...(selectProps.sx ? selectProps.sx : {}),
                    '&&& legend': placeholder
                        ? {
                              maxWidth: '100%',
                          }
                        : {},
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        border: '1px solid #DFE1ED',
                    },
                    '&& .MuiOutlinedInput-notchedOutline': {
                        ...(open && {
                            borderBottom: 'none',
                            borderBottomLeftRadius: 0,
                            borderBottomRightRadius: 0,
                        }),
                    },
                }}
                renderValue={() => {
                    const selectedValue =
                        value && Object.values(value).find(v => v);
                    if (!selectedValue && placeholder) {
                        return (
                            <Box
                                component="span"
                                sx={{
                                    color: '#949BAC',
                                }}
                            >
                                {placeholder}
                            </Box>
                        );
                    }

                    return formatValue(value);
                }}
                disabled={disabled}
                {...rest}
                value={''}
                MenuProps={{
                    PaperProps: {
                        sx: {
                            boxShadow: 0,
                            border: '1px solid #DFE1ED',
                            borderTop: 0,
                            borderRadius: '32px',
                            borderTopLeftRadius: 0,
                            borderTopRightRadius: 0,
                            marginTop: 0,
                            p: 2,
                            pt: 0,
                        },
                    },
                    MenuListProps: {
                        sx: {
                            borderTop: '#DFE1ED 1px solid',
                            py: 1,
                        },
                    },
                }}
            >
                {fields}
            </MuiSelect>
            <InputLabel
                required={!!required}
                shrink
                sx={{
                    '&.Mui-focused': {
                        color: '#949BAC',
                    },
                }}
            >
                {label}
            </InputLabel>
            {error && <FormHelperText>{error}</FormHelperText>}
        </FormControl>
    ) : (
        fields
    );
});
